import {
  AssignmentOutlined,
  EventBusy,
  FileOpenOutlined,
  FilterAlt,
  FilterAltOff,
  Schedule,
  TaskOutlined,
} from '@mui/icons-material';
import {
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Status } from '../WorkFlowHeader';
import './WorkFlowSelect.scss';

export type WorkFlowSelectProps = {
  scheduled?: number;
  open?: number;
  closed?: number;
  draft?: number;
  canceled?: number;
  options?: string[];
  status: string;
  onStatusClick: (status: Status) => void;
  onClearStatus?: () => void;
};
export const WorkFlowSelect: React.FC<WorkFlowSelectProps> = ({
  scheduled,
  closed,
  open,
  draft,
  canceled,
  status,
  onClearStatus,
  onStatusClick,
}) => {
  const { t } = useTranslation();

  const getStatusClickHandler = (status: Status) => () => {
    onStatusClick(status);
  };

  return (
    <div className="d-flex align-items-center mb-2">
      <IconButton disabled={!status} onClick={onClearStatus}>
        <FilterAlt
          color={status ? 'primary' : 'disabled'}
          sx={{ marginRight: 1 }}
          fontSize="large"
        />
      </IconButton>
      <FormControl fullWidth>
        <InputLabel size="normal" id="status-select-label">
          {t('placeholder_status')}
        </InputLabel>
        <Select
          color="primary"
          size="medium"
          labelId="status-select-label"
          id="status-select"
          className="workflow-select"
          value={status}
          inputProps={{
            className: 'workflow-select-input',
          }}
          MenuProps={{
            PaperProps: {
              className: 'workflow-status-select-paper',
            },
          }}
          label={t('placeholder_status')}
        >
          <MenuItem value="" onClick={onClearStatus}>
            <ListItemIcon>
              <FilterAltOff
                className="list-icon"
                fontSize="large"
                color="inherit"
              />
            </ListItemIcon>
            <ListItemText color="primary">
              {t('text_remove_status_filter')}
            </ListItemText>
          </MenuItem>
          {scheduled && (
            <MenuItem
              value={Status.Scheduled}
              className="scheduled"
              onClick={getStatusClickHandler(Status.Scheduled)}
            >
              <ListItemIcon className="scheduled">
                <Schedule fontSize="large" color="inherit" />
              </ListItemIcon>
              <ListItemText className="scheduled">
                {t('btn_scheduled')}
              </ListItemText>
              <Typography
                className="scheduled"
                variant="inherit"
                color="inherit"
              >
                {scheduled.toLocaleString('en-US', {
                  minimumIntegerDigits: 2,
                  useGrouping: false,
                })}
              </Typography>
            </MenuItem>
          )}
          {open && (
            <MenuItem
              value={Status.Open}
              onClick={getStatusClickHandler(Status.Open)}
            >
              <ListItemIcon className="open">
                <FileOpenOutlined fontSize="large" color="inherit" />
              </ListItemIcon>
              <ListItemText className="open" color="primary">
                {t('btn_open')}
              </ListItemText>
              <Typography className="open" variant="inherit" color="inherit">
                {open.toLocaleString('en-US', {
                  minimumIntegerDigits: 2,
                  useGrouping: false,
                })}
              </Typography>
            </MenuItem>
          )}
          {draft && (
            <MenuItem
              value={Status.Draft}
              onClick={getStatusClickHandler(Status.Draft)}
            >
              <ListItemIcon className="draft">
                <AssignmentOutlined fontSize="large" color="inherit" />
              </ListItemIcon>
              <ListItemText className="draft" color="primary">
                {t('btn_draft')}
              </ListItemText>
              <Typography className="draft" variant="inherit" color="inherit">
                {draft.toLocaleString('en-US', {
                  minimumIntegerDigits: 2,
                  useGrouping: false,
                })}
              </Typography>
            </MenuItem>
          )}
          {closed && (
            <MenuItem
              value={Status.Closed}
              onClick={getStatusClickHandler(Status.Closed)}
            >
              <ListItemIcon className="closed">
                <TaskOutlined fontSize="large" color="inherit" />
              </ListItemIcon>
              <ListItemText className="closed" color="primary">
                {t('btn_closed')}
              </ListItemText>
              <Typography className="closed" variant="inherit" color="inherit">
                {closed.toLocaleString('en-US', {
                  minimumIntegerDigits: 2,
                  useGrouping: false,
                })}
              </Typography>
            </MenuItem>
          )}
          {canceled && (
            <MenuItem
              value={Status.Canceled}
              onClick={getStatusClickHandler(Status.Canceled)}
            >
              <ListItemIcon className="canceled">
                <EventBusy fontSize="large" color="inherit" />
              </ListItemIcon>
              <ListItemText className="canceled" color="primary">
                {t('btn_canceled')}
              </ListItemText>
              <Typography
                className="canceled"
                variant="inherit"
                color="inherit"
              >
                {canceled.toLocaleString('en-US', {
                  minimumIntegerDigits: 2,
                  useGrouping: false,
                })}
              </Typography>
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </div>
  );
};
